import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import { getAccentTheme } from "@/lib/fokustypTheme";
import { BRAND } from "@/config/brand";
import {
  EuleGlyph,
  BieneGlyph,
  KatzeGlyph,
  HundGlyph,
} from "@/components/FokustypGlyphs";
import { api } from "@/lib/api";
import {
  ArrowLeft,
  ArrowRight,
  Lock,
  Loader2,
  Sparkles,
  Eye,
  HelpCircle,
} from "lucide-react";
import { FOKUSTYP_NAME, W_FRAGE_BY_FOKUSTYP } from "@/data/quickTestData";
import {
  KONFLIKTPOTENZIAL_BY_FOKUSTYP,
  KONFLIKTPOTENZIAL_PAARE_BY_FOKUSTYP,
  NAME_PHRASE_BY_FOKUSTYP,
  CHARAKTERISIERUNG_BY_FOKUSTYP,
  getKonfliktSeverity,
} from "@/data/dashboardData";
import { isFree as _isFree } from "@/lib/licensing";
import FokustypErkennungsmerkmale from "@/components/FokustypErkennungsmerkmale";
import ReflexionsVorschlaege from "@/components/ReflexionsVorschlaege";
import FokustypMatrix2x2 from "@/components/FokustypMatrix2x2";
import { STECKBRIEF_ERKENNUNG } from "@/lib/steckbriefErkennung";

const GLYPH = { eule: EuleGlyph, biene: BieneGlyph, katze: KatzeGlyph, hund: HundGlyph };
const VALID = ["katze", "eule", "biene", "hund"];
const DIAGONALE = { eule: "hund", biene: "katze", katze: "biene", hund: "eule" };
const FOKUSTYP_ARTIKEL = {
  eule: "eine Eule",
  biene: "eine Biene",
  katze: "eine Katze",
  hund: "einen Hund",
};

// --- Generic prose section ------------------------------------------------
// Issue 36 (04.09.2026) — Empty-Guard: leere Sektion ausblenden (kein Skelett).
function ProseSection({ eyebrow, title, children, testId, hideIfEmpty = true }) {
  // Prüfe ob children effektiv leer sind (kein renderbarer Text)
  const hasContent = React.Children.toArray(children).some((c) => {
    if (c == null || c === false || c === "") return false;
    if (typeof c === "string") return c.trim().length > 0;
    if (React.isValidElement(c)) {
      const cc = c.props?.children;
      if (typeof cc === "string") return cc.trim().length > 0;
      if (Array.isArray(cc)) return cc.some((x) => (typeof x === "string" ? x.trim().length > 0 : !!x));
      return cc != null && cc !== "";
    }
    return true;
  });
  if (hideIfEmpty && !hasContent) return null;
  return (
    <section
      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-7 sm:p-8"
      data-testid={testId}
    >
      {eyebrow && (
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
          {eyebrow}
        </p>
      )}
      <h3 className="mt-2 text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
        {title}
      </h3>
      <div className="mt-3 text-base text-slate-700 dark:text-slate-200 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

// --- Sektion: Hero (Eigener Steckbrief / Preview) -------------------------
// Sprint 4d AC #237 — Visuelle Differenzierung Preview vs. Eigener FT:
//   locked=true → „Vorschau: {Name}" + Lock-Icon neben Glyph
//   locked=false → „Dein dominierender Anteil: {Name}" + prominenter Glyph
// Sprint Launch-Blocker L-4 (02.09.2026):
//   Sekundärer Fokustyp bekommt eigenen Label „sekundärer Anteil"
//   (statt „dominierender Anteil" — sonst falsche Aussage über User-Typ).
function HeroSection({ slug, name, fokusfrage, einzeiler, locked = false, isSecondary = false, isForeign = false }) {
  const Glyph = GLYPH[slug] || EuleGlyph;
  // Launch-Blocker L-4 (02.09.2026) + v1.6 Iter-63 Fix:
  //   - Eigener Primärfokustyp: „Dein dominierender Anteil ist X"
  //   - Sekundärer Anteil:       „Dein sekundärer Anteil ist X"
  //   - Fremder FT (Vorschau/Pro-Browsing): „So tickt X"
  // WICHTIG: NICHT mehr an `locked` gekoppelt — Pro-User haben locked=false,
  // dürfen aber auf fremden FTs auch nicht „dominierender Anteil ist X" sehen.
  let headline;
  if (isSecondary) {
    headline = `Dein sekundärer Anteil ist ${name}`;
  } else if (isForeign || locked) {
    headline = `So tickt ${name}`;
  } else {
    // Sprint V19 Schicht 3 Komponente 3 (Boardroom-CoS Mo 07.09.2026 17:00 Europe/Berlin)
    // — Sprachregel „Dein dominierender Fokus-Typ ist Typ X" konsistent auf allen Routen.
    headline = `Dein dominierender Fokus-Typ ist Typ ${name}`;
  }
  const charakter = CHARAKTERISIERUNG_BY_FOKUSTYP[slug] || einzeiler;
  const accentTheme = getAccentTheme(slug);
  return (
    <section
      className="relative rounded-2xl bg-white dark:bg-slate-900 border border-[var(--fts-neutral-200)] dark:border-slate-800 p-8 overflow-hidden"
      data-testid="steckbrief-hero"
      data-locked={locked ? "true" : "false"}
      data-mode={locked ? "preview" : isSecondary ? "secondary" : isForeign ? "foreign" : "primary"}
    >
      {/* Sprint V36 (Sa 14.09.2026) — Fokustyp-Akzent-Top-Line
          für visuelle Konsistenz mit SteckbriefKachel auf /mein-muster. */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 ${accentTheme.gradient} opacity-80`}
        aria-hidden="true"
      />
      {/* Sprint 4d AC #237 + L-4 (02.09.2026) — Header-Label oben */}
      <p
        className={`text-xs font-bold uppercase tracking-widest mb-3 flex items-center gap-2 ${
          locked ? "text-[var(--fts-accent)]" : "text-[var(--fts-primary-bright)]"
        }`}
        data-testid="steckbrief-hero-mode-label"
      >
        {locked ? (
          <>
            <Lock className="w-3.5 h-3.5" /> Vorschau: {name}
          </>
        ) : isSecondary ? (
          <>Dein sekundärer Anteil: {name}</>
        ) : isForeign ? (
          <>So tickt: {name}</>
        ) : (
          <>Dein dominierender Fokus-Typ: Typ {name}</>
        )}
      </p>
      <div className="flex items-start gap-6 flex-col sm:flex-row">
        <div className={`relative ${locked ? "opacity-70" : ""}`}>
          <div className="hidden sm:block">
            <Glyph size={120} />
          </div>
          <div className="block sm:hidden">
            <Glyph size={80} />
          </div>
          {locked && (
            <span
              className="absolute -top-1 -right-1 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--fts-accent)] text-white shadow-md"
              data-testid="steckbrief-hero-lock-badge"
              aria-hidden="true"
            >
              <Lock className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
        <div className="flex-1">
          <h1
            className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white"
            data-testid="steckbrief-hero-headline"
          >
            {headline}
          </h1>
          <p
            className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400"
            data-testid="steckbrief-fokusfrage-lead"
          >
            Das ist die Frage, die du dir in jeder Situation zuerst stellst:
          </p>
          <p
            className="mt-1 text-base sm:text-lg text-[var(--fts-primary-bright)] font-semibold italic"
            data-testid="steckbrief-fokusfrage"
          >
            „{fokusfrage}"
          </p>
          <p
            className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400"
            data-testid="steckbrief-charakter-lead"
          >
            Hier ist, was dich ausmacht — kurz und ehrlich:
          </p>
          <p className="mt-1 text-base text-slate-700 dark:text-slate-200 leading-relaxed" data-testid="steckbrief-charakter">
            {charakter}
          </p>
        </div>
      </div>
    </section>
  );
}

// --- Sektion: Konfliktpotenzial — Sprint 4 Phase B: 3 Paare statt nur Diagonale ----
function KonfliktpotenzialSection({ slug }) {
  const paare = KONFLIKTPOTENZIAL_PAARE_BY_FOKUSTYP[slug];
  if (!paare) return null;
  // 3 Ziel-Fokustypen (alle anderen außer slug selbst)
  const zielSlugs = ["katze", "eule", "biene", "hund"].filter((z) => z !== slug);
  return (
    <section
      className="rounded-2xl bg-[var(--fts-neutral-50)] border border-[var(--fts-neutral-200)] p-7 sm:p-8"
      data-testid="steckbrief-konfliktpotenzial"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-accent)]">
            Spiegel
          </p>
          <h3
            className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--fts-text-primary)]"
            data-testid="steckbrief-konfliktpotenzial-headline"
          >
            Konfliktpotenzial
          </h3>
          <p className="mt-1 text-sm text-[var(--fts-text-muted)]">
            Wie du anderen Fokustypen sinnvoll begegnest — 3 alltagsnahe Paare.
          </p>
        </div>
      </div>
      <div className="mt-5 space-y-4">
        {zielSlugs.map((zielSlug) => {
          const text = paare[zielSlug];
          if (!text) return null;
          const ZielGlyph = GLYPH[zielSlug];
          const zielName = FOKUSTYP_NAME[zielSlug];
          // V1.G (Launch-Consolidation, 30.08.2026) — Diagonal-Paare
          // (katze↔biene, eule↔hund) werden als "kritisch" markiert.
          const severity = getKonfliktSeverity(slug, zielSlug);
          const isKritisch = severity === "kritisch";
          return (
            <div
              key={zielSlug}
              data-testid={`steckbrief-konfliktpotenzial-paar-${zielSlug}`}
              data-severity={severity}
              className={`rounded-xl bg-white dark:bg-slate-900 border-l-4 p-5 flex gap-3 items-start ${
                isKritisch ? "border-red-500" : "border-[var(--fts-primary-deep)]"
              }`}
            >
              {ZielGlyph && (
                <div className="flex-shrink-0" data-testid={`konflikt-paar-glyph-${zielSlug}`}>
                  <ZielGlyph size={32} />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-deep)]">
                    {FOKUSTYP_NAME[slug]} {String.fromCharCode(0x2192)} {zielName}
                  </p>
                  {isKritisch && (
                    <span
                      data-testid={`konflikt-paar-severity-${zielSlug}`}
                      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700"
                    >
                      Kritisch
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-[var(--fts-text-primary)] leading-relaxed">
                  {text}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// --- Sektion: 3 Ebenen der Kommunikation ----------------------------------
function DreiEbenenSection({ kontext, ebenen }) {
  if (!ebenen) return null;
  return (
    <ProseSection
      eyebrow="Wirkungsebenen"
      title="3 Ebenen der Kommunikation"
      testId="steckbrief-drei-ebenen"
    >
      {kontext && (
        <p className="text-sm text-[var(--fts-text-muted)]" data-testid="steckbrief-drei-ebenen-kontext">
          {kontext}
        </p>
      )}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-5">
        <div data-testid="steckbrief-drei-ebenen-inhalt">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Inhalt
          </p>
          <p className="mt-1 text-sm leading-relaxed">{ebenen.inhalt_7_prozent}</p>
        </div>
        <div data-testid="steckbrief-drei-ebenen-tonalitaet">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Tonalität
          </p>
          <p className="mt-1 text-sm leading-relaxed">{ebenen.tonalitaet_38_prozent}</p>
        </div>
        <div data-testid="steckbrief-drei-ebenen-koerper">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Körpersprache
          </p>
          <p className="mt-1 text-sm leading-relaxed">{ebenen.koerpersprache_55_prozent}</p>
        </div>
      </div>
    </ProseSection>
  );
}

// --- Sektion: 3 Sofort-Tipps (Sprint 3a — immer sichtbar fuer Premium-User) -
// Zeigt, wie der USER seines aktuellen Steckbrief-Fokustyps mit den ANDEREN
// 3 Fokustypen umgeht. Beispiel im Hund-Steckbrief: "Wenn du mit einer Katze
// sprichst: ..." / Eule / Biene.
function SofortTippsSection({ slug, tipps, name }) {
  if (!tipps || tipps.length === 0) return null;
  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-[var(--fts-success)] p-7 sm:p-8"
      data-testid="steckbrief-sofort-tipps"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-success)]">
        3 Sofort-Tipps
      </p>
      <h3 className="mt-2 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
        Was brauchen die anderen 3 Fokustypen von dir?
      </h3>
      <p className="mt-1 text-sm text-[var(--fts-text-muted)]">
        Empfänger-Perspektive: Was braucht dein Gegenüber, damit dein Flexpfad ankommt? Drei Tipps für dich als {name}.
      </p>
      <ol className="mt-4 space-y-4">
        {tipps.map((t, i) => {
          const ZielGlyph = GLYPH[t.ziel_fokustyp];
          return (
            <li
              key={`${t.ziel_fokustyp}-${i}`}
              data-testid={`steckbrief-sofort-tipp-${t.ziel_fokustyp}`}
              className="flex gap-3"
            >
              <span className="w-9 h-9 flex-shrink-0 rounded-full bg-[var(--fts-neutral-50)] flex items-center justify-center">
                {ZielGlyph ? <ZielGlyph size={22} /> : null}
              </span>
              <div className="flex-1">
                <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                  Was braucht {FOKUSTYP_ARTIKEL[t.ziel_fokustyp] || t.ziel_fokustyp}?
                </p>
                <p className="mt-1 text-base text-[var(--fts-text-primary)] leading-relaxed">
                  {t.text}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// --- Sektion: Magic Tipps (viewer-relative — Legacy, nur Konfliktpartner-Steckbrief)
function MagicTipsSection({ slug, tipps }) {
  if (!tipps || tipps.length === 0) return null;
  const name = FOKUSTYP_NAME[slug];
  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-[var(--fts-success)] p-7 sm:p-8"
      data-testid="steckbrief-magic-tipps"
    >
      <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-success)]">
        Magic Tipps
      </p>
      <h3 className="mt-2 text-xl sm:text-2xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
        Wie spricht jemand mit {slug === "hund" ? "einem Hund" : `einer ${name}`}?
      </h3>
      <p className="mt-1 text-sm text-[var(--fts-text-muted)]">
        4 Tipps für jemanden, der gerade bei dir steht. Frank-Stil — alltagsnah, sofort verwendbar.
      </p>
      <ol className="mt-4 space-y-4">
        {tipps.map((t, i) => (
          <li
            key={i}
            data-testid={`steckbrief-magic-tipp-${i + 1}`}
            className="flex gap-3"
          >
            <span className="w-7 h-7 flex-shrink-0 rounded-full bg-[var(--fts-success)] text-white font-bold text-xs flex items-center justify-center">
              {i + 1}
            </span>
            <span className="text-base text-[var(--fts-text-primary)] leading-relaxed pt-0.5">
              {t}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

// --- Sektion: Spiegel (mit Alltags-Beispiel) ------------------------------
function SpiegelSection({ spiegel }) {
  if (!spiegel) return null;
  return (
    <section
      className="rounded-2xl bg-[var(--fts-primary-deep)] text-white p-7 sm:p-8"
      data-testid="steckbrief-spiegel"
    >
      <div className="flex items-start gap-3">
        <Eye className="w-5 h-5 mt-1 flex-shrink-0 opacity-80" />
        <div className="flex-1">
          <p className="text-xs font-bold uppercase tracking-widest opacity-80">
            Spiegel
          </p>
          <h3 className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tight">
            Was du tun kannst — und was du beobachten wirst
          </h3>
          <p className="mt-3 text-base leading-relaxed" data-testid="steckbrief-spiegel-tun">
            {spiegel.tun}
          </p>
          <p className="mt-3 text-base leading-relaxed opacity-90" data-testid="steckbrief-spiegel-beobachten">
            {spiegel.beobachten}
          </p>
          {spiegel.beispiel && (
            <div
              className="mt-5 rounded-xl bg-white/10 border border-white/20 p-4"
              data-testid="steckbrief-spiegel-beispiel"
            >
              <p className="text-xs font-bold uppercase tracking-widest opacity-80">
                Beispiel
              </p>
              <p className="mt-1 text-sm leading-relaxed">{spiegel.beispiel}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// --- Sektion: Brücken-Frage (M6 POSTLAUNCH-V3, emotionaler Anker) -----------
function BrueckenFrageSection({ slug }) {
  const erk = STECKBRIEF_ERKENNUNG[slug];
  if (!erk || !erk.bruecken_frage) return null;
  return (
    <section
      className="rounded-2xl bg-[var(--fts-primary-bright)]/10 border-l-4 border-[var(--fts-primary-bright)] p-5 sm:p-6"
      data-testid={`steckbrief-detail-bruecken-frage-${slug}`}
    >
      <div className="flex items-start gap-3">
        <HelpCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-[var(--fts-primary-bright)]" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Wen erkennst du wieder?
          </p>
          <p className="mt-2 text-base text-slate-800 dark:text-slate-100 leading-relaxed">
            {erk.bruecken_frage}
          </p>
        </div>
      </div>
    </section>
  );
}

// --- Page -----------------------------------------------------------------
export default function DashboardFokustypDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [steckbrief, setSteckbrief] = useState(null);
  const [loading, setLoading] = useState(VALID.includes(slug));
  const [error, setError] = useState(null);
  const isValidSlug = VALID.includes(slug);

  useEffect(() => {
    if (!isValidSlug) return undefined;
    let cancelled = false;
    setLoading(true);
    api.get(`/fokustyp/${slug}`).then(
      ({ data: payload }) => {
        if (!cancelled) {
          setSteckbrief(payload);
          setLoading(false);
        }
      },
      (e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 401) {
          navigate(`/login?next=${encodeURIComponent(`/dashboard/fokustyp/${slug}`)}`, { replace: true });
          return;
        }
        // Sprint 3b L.2b — Free-User auf Nicht-Primaer → Redirect zu Dashboard mit Hinweis
        if (status === 403) {
          const code = e?.response?.data?.detail?.code;
          if (code === "free_user_other_fokustyp_locked") {
            try {
              sessionStorage.setItem(
                "steckbrief_locked_toast",
                "Diesen Steckbrief schaltest du mit Pro Solo frei.",
              );
            } catch (err) { /* ignore */ }
            navigate("/dashboard?upsell=steckbrief-locked", { replace: true });
            return;
          }
        }
        setError(status === 404 ? "not_found" : "error");
        setLoading(false);
      }
    );
    return () => { cancelled = true; };
  }, [slug, navigate, isValidSlug]);

  return (
    <RedesignLayout>
      {({ data: layoutData }) => {
        if (loading) {
          return (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 text-[var(--fts-primary-bright)] animate-spin" data-testid="steckbrief-detail-loading" />
            </div>
          );
        }
        if (error === "not_found" || !isValidSlug) {
          return (
            <div className="max-w-2xl mx-auto py-20 text-center px-6" data-testid="steckbrief-detail-404">
              <h1 className="text-2xl font-bold text-[var(--fts-text-primary)]">Fokustyp nicht gefunden</h1>
              <button
                onClick={() => navigate("/dashboard/fokustyp")}
                className="mt-6 inline-flex items-center gap-1 rounded-lg bg-[var(--fts-primary-bright)] text-white font-semibold px-4 py-2 text-sm"
                data-testid="steckbrief-detail-404-back"
              >
                <ArrowLeft className="w-4 h-4" /> Zurück zur Übersicht
              </button>
            </div>
          );
        }
        if (!steckbrief) return null;
        // Sprint v1.6 Plan-Konsistenz-Check 2.0 (Mi 02.09.2026) — Akuter Fix
        // Bereich 1: Pro-User dürfen KEINEN „vollständig freischalten"-Paywall
        // mehr sehen. Backend markiert non-primary FTs als locked=true, das
        // ist visuell für Pro-Solo aber falsch (sie haben schon den höchsten Plan).
        const isFreeUser = _isFree(layoutData?.user);
        const backendLocked = steckbrief.zugaenglich === false;
        // `locked` = Entitlement-Gate (nur Free-User → Paywall zeigen)
        const locked = backendLocked && isFreeUser;
        const isOwn = layoutData?.user?.primary_fokustyp === steckbrief.fokustyp;
        // `isForeign` = Anzeige-Flag: fremder FT (kein primary, kein secondary).
        // Testing-Agent-Fix (v1.6 Iter-63): getrennt von `locked`, damit
        // Pro-User auf fremdem FT den neutralen „So tickt X" Text sehen.
        const isForeignForDisplay = !isOwn;
        const viewerPrimary = layoutData?.user?.primary_fokustyp;
        // Launch-Blocker L-4 (02.09.2026) + Sprint v1.6 Iter-63 Fix:
        // Sekundärfokustyp: user.secondary_fokustyp == aktueller slug UND
        // nicht primary. Nicht mehr an `locked` gekoppelt (Pro-User hat locked=false).
        const isSecondary =
          !isOwn &&
          !!layoutData?.user?.secondary_fokustyp &&
          layoutData.user.secondary_fokustyp === steckbrief.fokustyp;
        // Master-Prompt v2.8 §3.2 — Magic Tipps erscheinen NUR im Konfliktpartner-
        // Steckbrief des Viewers. D.h. wenn slug == viewer.primary's diagonale.
        // Tipps beschreiben „wie spricht jemand mit diesem slug-Fokustyp" und
        // sind nutzbar, wenn der Viewer (=Partner) den Steckbrief seines
        // Konfliktpartners liest und dort die Tipps für seinen eigenen Umgang
        // mit ihm sieht.
        const isViewerKonfliktpartner =
          !!viewerPrimary && DIAGONALE[viewerPrimary] === steckbrief.fokustyp;

        return (
          <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 space-y-6" data-testid="steckbrief-detail-page">
            {/* Issue 18: „Zurück zu Mein Gegenüber"-Link im Redesign-Kontext */}
            <button
              type="button"
              onClick={() => navigate("/andere-verstehen")}
              data-testid="steckbrief-detail-back-to-andere"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-[var(--fts-primary-bright)]"
            >
              <ArrowLeft className="w-4 h-4" /> Zurück zu Mein Gegenüber
            </button>

            <HeroSection
              slug={steckbrief.fokustyp}
              name={steckbrief.name}
              fokusfrage={steckbrief.fokusfrage}
              einzeiler={steckbrief.einzeiler}
              locked={locked}
              isSecondary={isSecondary}
              isForeign={isForeignForDisplay && !isSecondary}
            />

            {/* Sprint 11.6 Block 2 (Task 3) — 5 Erkennungsmerkmale + 2x2-Matrix.
                Beide sind ALLEN sichtbar (auch im Lock-Mode), weil sie alltags-
                taugliche Orientierung sind und keine Pro-Tiefe enthalten. */}
            <FokustypErkennungsmerkmale fokustyp={steckbrief.fokustyp} />
            <FokustypMatrix2x2
              primaryFokustyp={viewerPrimary || steckbrief.fokustyp}
              secondaryFokustyp={layoutData?.user?.secondary_fokustyp}
            />

            {/* Sprint 4d AC #235 — Konfliktpotenzial nur für eigenen FT sichtbar (nicht in Preview).
                Preview zeigt nur Hero + Teaser-Liste. */}
            {!locked && <KonfliktpotenzialSection slug={steckbrief.fokustyp} />}
            {/* Sprint M5.42 (30.07.2026) — Block A6: Reflexions-Vorschläge
                (M-22/M-23 Vertiefung) sind Pro-only, auch für den eigenen FT.
                Free-User bekommt den Impuls-Block nicht (Conversion-Anker
                bleibt "Mehr Profil-Tiefe mit Pro Solo" in FreeFokustypBibliothek). */}
            {!locked && !_isFree(layoutData?.user) && (
              <ReflexionsVorschlaege fokustyp={steckbrief.fokustyp} />
            )}

            {locked && (
              <section
                className="rounded-2xl border-2 border-dashed border-[var(--fts-accent)] bg-amber-50/40 p-7 sm:p-8"
                data-testid="steckbrief-paywall"
              >
                <div className="flex items-start gap-3">
                  <Lock className="w-6 h-6 text-[var(--fts-accent)] mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h3
                      className="text-lg sm:text-xl font-extrabold text-[var(--fts-text-primary)]"
                      data-testid="steckbrief-paywall-headline"
                    >
                      {steckbrief.name}-Steckbrief vollständig freischalten
                    </h3>
                    <p
                      className="mt-2 text-sm text-[var(--fts-text-primary)] leading-relaxed"
                      data-testid="steckbrief-paywall-teaser"
                    >
                      Konfliktpotenzial, 3 Sofort-Tipps und die Flexpfad-Architektur für{" "}
                      {FOKUSTYP_ARTIKEL[steckbrief.fokustyp]} — direkt morgen einsetzbar.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/dashboard/abo")}
                      data-testid="steckbrief-paywall-cta"
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--fts-accent)] hover:opacity-90 text-white font-bold px-5 py-2.5 text-sm transition-opacity shadow-sm"
                    >
                      <Lock className="w-4 h-4" /> {BRAND.name} Pro Solo · 39 €/Monat
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </section>
            )}

            {!locked && (
              <>
                <ProseSection
                  eyebrow="Motivation"
                  title={`Faktoren für ${
                    ["eule", "katze"].includes(steckbrief.fokustyp)
                      ? "das Wollen oder Nichtwollen"
                      : "das Können oder Nichtkönnen"
                  }`}
                  testId="steckbrief-wollen"
                >
                  {steckbrief.motivation_sub_lead && (
                    <p className="text-sm text-[var(--fts-text-muted)] italic mb-3">
                      {steckbrief.motivation_sub_lead}
                    </p>
                  )}
                  <p>{steckbrief.faktoren_wollen}</p>
                </ProseSection>

                <ProseSection
                  eyebrow="Stress"
                  title="Stress-Faktoren"
                  testId="steckbrief-stress"
                >
                  <p>{steckbrief.faktoren_reaktion}</p>
                </ProseSection>

                <DreiEbenenSection
                  kontext={steckbrief.drei_ebenen_kontext}
                  ebenen={steckbrief.drei_ebenen}
                />

                <ProseSection
                  eyebrow="Erkennung"
                  title={`Woran erkenne ich ${
                    steckbrief.fokustyp === "hund" ? "einen Hund" : `eine ${steckbrief.name}`
                  }?`}
                  testId="steckbrief-erkennungsmerkmale"
                >
                  <p>{steckbrief.erkennungsmerkmale}</p>
                </ProseSection>

                {/* Sprint 3a — Sofort-Tipps (eigene Perspektive, 3 Tipps fuer die anderen 3 Fokustypen) */}
                <SofortTippsSection
                  slug={steckbrief.fokustyp}
                  name={steckbrief.name}
                  tipps={steckbrief.sofort_tipps}
                />

                <SpiegelSection spiegel={steckbrief.spiegel} />

                {/* M6 POSTLAUNCH-V3 — Brücken-Frage als emotionaler Anker */}
                <BrueckenFrageSection slug={steckbrief.fokustyp} />

                {/* Cross-sell für Free-User auf eigenem Fokustyp (Sprint 4d-Update). */}
                {/* Refactor Feb 2026: licensing.js single source of truth. */}
                {isOwn && _isFree(layoutData?.user) && (
                  <section
                    className="rounded-2xl bg-[var(--fts-primary-deep)] text-white p-7"
                    data-testid="steckbrief-cross-sell"
                  >
                    <div className="flex items-start gap-4 flex-col md:flex-row md:items-center">
                      <Sparkles className="w-6 h-6 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold">Du willst alle 4 Fokustypen + das Botschaften-Modul?</h3>
                        <p className="mt-1 text-sm opacity-90">Mit Pro Solo schaltest du die anderen 3 Steckbriefe und das Botschaften-Modul unbegrenzt frei.</p>
                      </div>
                      <button
                        onClick={() => navigate("/dashboard/abo")}
                        data-testid="steckbrief-cross-sell-cta"
                        className="rounded-xl bg-white text-[var(--fts-primary-deep)] font-bold px-5 py-3 text-sm hover:bg-slate-50"
                      >
                        Pro Solo starten
                      </button>
                    </div>
                  </section>
                )}
                {/* Sprint 4d AC #238 — Inline-Hinweis für eigenen FT (kein Modal).
                    Sichtbar wenn Free-User auf eigenem FT — Sprint 4d Fix: backend
                    liefert zugaenglich_grund=null für full_access. Wir checken stattdessen
                    direkt isOwn + Free-Tier. */}
                {isOwn && _isFree(layoutData?.user) && (
                  <p
                    className="text-xs text-stone-500 italic text-center"
                    data-testid="steckbrief-eigener-inline-hint"
                  >
                    Mehr Tiefe? Mit Pro Solo schaltest du Co-Creator + die anderen 3 Steckbriefe frei.
                  </p>
                )}
              </>
            )}
          </div>
        );
      }}
    </RedesignLayout>
  );
}
